import React, { useEffect, useState } from "react";
import { api, rupiah } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, Wallet, AlertTriangle, Store, Sparkles, Loader2, TrendingUp, BarChart3, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const MONTHS_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MONTHS_ID = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const mLabel = (p) => { const [y, m] = (p || "").split("-"); return m ? `${MONTHS_SHORT[Number(m)]} ${String(y).slice(2)}` : p; };
const mLong = (p) => { const [y, m] = (p || "").split("-"); return m ? `${MONTHS_ID[Number(m)]} ${y}` : p; };
const lastMonths = (n) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) { const dd = new Date(d.getFullYear(), d.getMonth() - i, 1); out.push(`${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}`); }
  return out;
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("6");
  const [expMonth, setExpMonth] = useState(lastMonths(1)[0]);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    api.get(`/admin/stats?months=${range}`).then((r) => setStats(r.data)).catch(() => {});
  }, [range]);

  const genInsight = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/admin-insight");
      setInsight(data.insight);
    } catch (e) {
      toast.error("Gagal memuat insight AI");
    } finally {
      setLoading(false);
    }
  };

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const r = await api.get(`/admin/finance-summary/export?month=${expMonth}&format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ringkasan_keuangan_${expMonth}.${fmt === "xlsx" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh ringkasan"); }
    finally { setExporting(null); }
  };

  const rate = stats && stats.total_billed > 0 ? Math.round((stats.total_collected / stats.total_billed) * 100) : 0;
  const trendData = (stats?.trend || []).map((t) => ({ ...t, label: mLabel(t.month) }));

  return (
    <div>
      <PageHeader
        title="Dashboard Keuangan"
        subtitle={stats ? `Tahun Pelajaran ${stats.academic_year} · Semester ${stats.semester}` : "Memuat…"}
        action={
          <div className="flex items-center gap-2">
            <Select value={expMonth} onValueChange={setExpMonth}>
              <SelectTrigger className="rounded-xl bg-white h-10 w-40" data-testid="export-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{lastMonths(12).map((m) => <SelectItem key={m} value={m}>{mLong(m)}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => exportFile("pdf")} disabled={!!exporting} data-testid="export-finance-pdf" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
              {exporting === "pdf" ? <Loader2 size={16} className="animate-spin" /> : (<><FileText size={16} className="mr-1.5" /> PDF</>)}
            </Button>
            <Button onClick={() => exportFile("xlsx")} disabled={!!exporting} data-testid="export-finance-xlsx" variant="outline" className="rounded-xl">
              {exporting === "xlsx" ? <Loader2 size={16} className="animate-spin" /> : (<><FileSpreadsheet size={16} className="mr-1.5" /> Excel</>)}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Pemasukan Bulan Ini" value={stats ? rupiah(stats.income_month) : "–"} sub="Tagihan, top-up & tabungan" />
        <StatCard icon={Wallet} label="Total Terkumpul" value={stats ? rupiah(stats.total_collected) : "–"} accent="blue" sub={`${rate}% dari total tagihan`} />
        <StatCard icon={AlertTriangle} label="Tunggakan" value={stats ? rupiah(stats.arrears) : "–"} accent="rose" sub={stats ? `${rupiah(stats.overdue_arrears)} sudah jatuh tempo` : ""} />
        <StatCard icon={Store} label="Belanja Kopontren (Bln Ini)" value={stats ? rupiah(stats.belanja_month) : "–"} accent="amber" sub={`Total ${stats ? rupiah(stats.kopontren_revenue) : "–"}`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <p className="font-heading font-semibold text-stone-900 flex items-center gap-2"><BarChart3 size={18} className="text-emerald-600" /> Tren Pemasukan vs Belanja</p>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="rounded-xl bg-white h-9 w-32" data-testid="trend-range-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 Bulan</SelectItem>
                <SelectItem value="6">6 Bulan</SelectItem>
                <SelectItem value="12">12 Bulan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {trendData.length > 0 ? (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={trendData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0efe9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#78716c" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(0)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v)} tick={{ fontSize: 11, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip formatter={(v) => rupiah(v)} cursor={{ fill: "#f5f5f4" }} contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 13 }} />
                  <Legend wrapperStyle={{ fontSize: 13 }} />
                  <Bar dataKey="pemasukan" name="Pemasukan" fill="#059669" radius={[6, 6, 0, 0]} maxBarSize={46} />
                  <Bar dataKey="belanja" name="Belanja Kopontren" fill="#f59e0b" radius={[6, 6, 0, 0]} maxBarSize={46} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm flex flex-col">
          <p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">Capaian Penagihan</p>
          <div className="flex items-end gap-2">
            <span className="font-heading text-4xl font-bold text-emerald-600">{rate}%</span>
            <span className="text-stone-400 mb-1 text-sm">tercapai</span>
          </div>
          <div className="mt-4 h-3 rounded-full bg-stone-100 overflow-hidden">
            <div className="h-full bg-emerald-600 rounded-full transition-all" style={{ width: `${rate}%` }} />
          </div>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex items-center justify-between"><span className="text-stone-500 flex items-center gap-1.5"><GraduationCap size={15} /> Total Santri</span><b className="text-stone-800">{stats?.total_students ?? "–"}</b></div>
            <div className="flex items-center justify-between"><span className="text-stone-500 flex items-center gap-1.5"><AlertTriangle size={15} className="text-rose-500" /> Tagihan belum lunas</span><b className="text-stone-800">{stats?.unpaid_count ?? 0}</b></div>
            <div className="flex items-center justify-between"><span className="text-stone-500 flex items-center gap-1.5"><AlertTriangle size={15} className="text-amber-500" /> Jatuh tempo</span><b className="text-stone-800">{stats?.overdue_count ?? 0} tagihan</b></div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-6 text-white shadow-lg mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={18} /> <p className="font-heading font-semibold">Insight AI</p>
          </div>
          <Button onClick={genInsight} disabled={loading} data-testid="ai-insight-button" size="sm" className="bg-white/15 hover:bg-white/25 rounded-lg">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Buat Laporan"}
          </Button>
        </div>
        {insight ? (
          <p className="text-emerald-50 leading-relaxed text-sm whitespace-pre-line" data-testid="ai-insight-text">{insight}</p>
        ) : (
          <p className="text-emerald-100/70 text-sm">Klik "Buat Laporan" untuk analisis penagihan, santri prioritas, dan kondisi kopontren oleh AI.</p>
        )}
      </div>
    </div>
  );
}
